import React from "react";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AnchorOutlinedIcon from "@mui/icons-material/AnchorOutlined";
import SentimentSatisfiedOutlinedIcon from "@mui/icons-material/SentimentSatisfiedOutlined";
import ChargingStationOutlinedIcon from "@mui/icons-material/ChargingStationOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import Tooltip from "@mui/material/Tooltip";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoIcon from "@mui/icons-material/Info";

function EmployeeProfileStatisticsTab(props) {
  const totalShiftsScheduled =
    props.selectedTeamMemberData?.published_schedules_count || 0;
  const calledOffSick =
    props.selectedTeamMemberData?.published_schedules_called_off_sick_count || 0;
  const calledOffOther =
    props.selectedTeamMemberData?.published_schedules_called_off_other_count || 0;
  const noCallNoShow =
    props.selectedTeamMemberData?.published_schedules_no_show_count || 0;
  const managerCancelled =
    props.selectedTeamMemberData?.published_schedules_manager_cancelled_count || 0;
  const missedShifts = calledOffSick + calledOffOther + noCallNoShow;
  const unmissedShifts =
    props.selectedTeamMemberData?.published_schedules_unmissed_count ??
    Math.max(totalShiftsScheduled - missedShifts - managerCancelled, 0);
  const reliabilityEligibleShifts = unmissedShifts + missedShifts;
  const totalShiftsWorked = unmissedShifts;
  const totalHoursWorked =
    props.selectedTeamMemberData?.published_schedules_hours_worked || 0;
  const averageShiftLength =
    totalShiftsWorked !== 0
      ? (totalHoursWorked / totalShiftsWorked).toFixed(2)
      : 0;
  const daysOffRequested =
    props.selectedTeamMemberData?.time_off_requests_approved_count;
  const tradesInitiated =
    props.selectedTeamMemberData?.shifts_swapped_dropped_count;
  const tradesAccepted =
    props.selectedTeamMemberData?.shifts_swapped_picked_up_count;
  const shiftsDropped = props.selectedTeamMemberData?.shifts_dropped_count;
  const shiftsPickedUp = props.selectedTeamMemberData?.shifts_picked_up_count;

  // Calculate reliability percentage
  const hasReliabilityScore = reliabilityEligibleShifts > 0;
  const reliabilityPercentage = hasReliabilityScore
    ? ((totalShiftsWorked / reliabilityEligibleShifts) * 100).toFixed(1)
    : null;
  const reliabilityDisplay = hasReliabilityScore
    ? `${reliabilityPercentage}%`
    : "N/A";
  const reliabilityColor = !hasReliabilityScore
    ? "text-gray-500"
    : reliabilityPercentage >= 90
    ? "text-green-600"
    : reliabilityPercentage >= 75
    ? "text-orange-600"
    : "text-red-600";

  const StatCard = ({ title, icon: IconComponent, children, helpText }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col">
      {/* Card Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {helpText && (
              <Tooltip title={helpText} arrow>
                <InfoIcon className="text-gray-500 cursor-help" style={{ fontSize: "18px" }} />
              </Tooltip>
            )}
          </div>
          <IconComponent className="text-gray-600" style={{ fontSize: "24px" }} />
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6 flex-1">
        {children}
      </div>
    </div>
  );

  const MetricRow = ({ label, value }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className="text-lg font-semibold text-gray-900 min-w-[3rem] text-right">{value}</span>
    </div>
  );

  const HighlightMetric = ({ value, label, color = "text-blue-600" }) => (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <div className={`text-3xl font-bold ${color}`}>{value}</div>
      <div className="text-sm text-gray-600 mt-1">{label}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <EmployeeProfileTabHeader
        icon={BarChartIcon}
        title="Performance Statistics"
        subtitle="Track work history, reliability, and activity metrics"
      />

      {/* Key Metrics Overview */}
      <div className="grid md:grid-cols-1 grid-cols-4 gap-4 mb-8">
        <HighlightMetric
          value={totalShiftsScheduled || 0}
          label="Total Shifts"
          color="text-blue-600"
        />
        <HighlightMetric
          value={totalHoursWorked ? totalHoursWorked.toFixed(1) : 0}
          label="Hours Worked"
          color="text-amber-500"
        />
        <HighlightMetric
          value={averageShiftLength}
          label="Avg. Shift (hrs)"
          color="text-purple-600"
        />
        <HighlightMetric
          value={reliabilityDisplay}
          label="Reliability"
          color={reliabilityColor}
        />
      </div>

      {/* Detailed Statistics Cards */}
      <div className="grid lg:grid-cols-1 grid-cols-3 gap-6">
        {/* Experience Card */}
        <StatCard
          title="Work Experience"
          icon={HowToRegOutlinedIcon}
        >
          <div className="space-y-1">
            <MetricRow
              label="Shifts Scheduled"
              value={totalShiftsScheduled || 0}
            />
            <MetricRow
              label="Shifts Worked"
              value={totalShiftsWorked || 0}
            />
            <MetricRow
              label="Total Hours"
              value={totalHoursWorked ? totalHoursWorked.toFixed(1) : 0}
            />
            <MetricRow
              label="Average Shift Length"
              value={`${averageShiftLength} hrs`}
            />
          </div>
        </StatCard>

        {/* Reliability Card */}
        <StatCard
          title="Reliability"
          icon={AnchorOutlinedIcon}
          helpText="To record a missed shift, open a past shift in the schedule and use the action menu in the shift details drawer."
        >
          <div className="space-y-1">
            <MetricRow
              label="Shifts Missed"
              value={`${missedShifts} out of ${reliabilityEligibleShifts}`}
            />
            <MetricRow
              label="Called Off Sick"
              value={`${calledOffSick} out of ${missedShifts}`}
            />
            <MetricRow
              label="Called Off Other"
              value={`${calledOffOther} out of ${missedShifts}`}
            />
            <MetricRow
              label="No-Call No-Show"
              value={`${noCallNoShow} out of ${missedShifts}`}
            />
            <MetricRow
              label="Manager Cancelled"
              value={managerCancelled || 0}
            />
          </div>
        </StatCard>

        {/* Activity Card */}
        <StatCard
          title="Activity & Engagement"
          icon={FormatListBulletedOutlinedIcon}
          helpText="These metrics only count successful instances of these actions. For example, a day off requested that was denied by the manager wouldn't count in 'Days off requested'."
        >
          <div className="space-y-1">
            <MetricRow
              label="Days Off Requested"
              value={daysOffRequested || 0}
            />
            <MetricRow
              label="Total Trades"
              value={(tradesInitiated + tradesAccepted) || 0}
            />
            <MetricRow
              label="Shifts Dropped"
              value={shiftsDropped || 0}
            />
            <MetricRow
              label="Shifts Picked Up"
              value={shiftsPickedUp || 0}
            />
          </div>
        </StatCard>
      </div>
    </div>
  );
}

export default EmployeeProfileStatisticsTab;
